import React from 'react'
import Helmet from 'react-helmet'
import { helmetJsonLdProp } from 'react-schemaorg'
import { getSrc } from 'gatsby-plugin-image'
import useSiteMetadata from '@helpers-blog/useSiteMetadata'
import getImageVariant from '@components/utils/getImageVariant'

// Michael Lu, 2024/10/15
// seoTitle
const Seo = ({
  title,
  seoTitle,
  description,
  excerpt,
  meta,
  keywords,
  author,
  category,
  date,
  timeToRead,
  children,
  thumbnail,
  siteUrl,
  locale,
  // 2024/10/27
  breadcrumbs,
  modifiedTimeUTC,
  // 2024/11/22
  cacheControls
}) => {
  const site = useSiteMetadata()

  const social = (author && author.social) || site.social || []
  const twitter =
    social.find(s => s.name && s.name.toLowerCase() === 'twitter') || {}

  description = excerpt || description || site.description

  const imageSrc = getSrc(getImageVariant(thumbnail, 'hero'))
  const imageUrl =
    imageSrc &&
    (imageSrc.startsWith('//')
      ? 'https:' + imageSrc
      : siteUrl && `${siteUrl}${imageSrc}`)

  /**
   * Meta Tags
   */

  const metaTags = [
    { itemprop: 'name', content: seoTitle || title || site.title },
    { itemprop: 'description', content: description },
    { name: 'description', content: description },

    { property: 'og:title', content: seoTitle || title || site.title },
    { property: 'og:description', content: description },
    { property: 'og:type', content: date ? 'article' : 'website' },
    { property: 'og:site_name', content: site.name },
    { property: 'og:image', content: imageUrl },

    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:site', content: site.name },
    { name: 'twitter:title', content: seoTitle || title },
    { name: 'twitter:description', content: description },
    { name: 'twitter:creator', content: twitter.url },
    { name: 'twitter:image', content: imageUrl }
  ]

  if (keywords && keywords.length > 0) {
    metaTags.push({ name: 'keywords', content: keywords.join(', ') })
  }

  if (date) {
    metaTags.push({ name: 'article:published_time', content: date })
  }

  if (timeToRead) {
    metaTags.push({ name: 'twitter:label1', value: 'Reading time' })
    metaTags.push({
      name: 'twitter:data1',
      value: `${timeToRead} min read`
    })
  }

  // 2024/11/22
  // Cache Controls
  // https://medium.com/@steelcityamir/a-web-developers-guide-to-browser-caching-cc41f3b73e7c
  // https://imagekit.io/blog/ultimate-guide-to-http-caching-for-static-assets/
  // 改用 Gatsby 設定
  // https://www.gatsbyjs.com/docs/how-to/previews-deploys-hosting/headers/
  if (cacheControls) {
    // metaTags.push({ name: 'Cache-Control', value: 'no-cache, no-store, must-revalidate' })
  } else {
    cacheControls = 'no-cache, no-store, must-revalidate'
  }

  if (meta) {
    metaTags.push(meta)
  }

  /**
   * Structured Data (JSON-LD)
   */

  const scripts = []

  // Article
  if (title && author) {
    const articleJsonLd = helmetJsonLdProp({
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: seoTitle || title,
      image: imageUrl,
      datePublished: modifiedTimeUTC,
      author: {
        '@type': 'Person',
        name: author.name,
        url: author.slug
      }
    })
    scripts.push(articleJsonLd)
  }

  // Breadcrumb
  if (breadcrumbs) {
    // let before = [
    //   '1', 'Lv1', '/1',
    //   '2', 'Lv2', '/2',
    //   '3', 'Lv3', '/3',
    //   '4', 'Lv4', '/4'];
    let after = breadcrumbs.reduce((acc, c, i, arr) => {
      if (i % 3 === 0 && i + 2 < arr.length) {
        acc.push({
          '@type': 'ListItem',
          position: Number(c),
          name: arr[i + 1],
          item: arr[i + 2]
        })
      }
      return acc
    }, [])

    // console.log(after);

    const breadcrumbJsonLd = helmetJsonLdProp({
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: after
    })
    scripts.push(breadcrumbJsonLd)
  } else if (title && category) {
    const breadcrumbJsonLd = helmetJsonLdProp({
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: site.name,
          item: siteUrl
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: category.name,
          item: `${siteUrl}${category.slug}`
        }
      ]
    })
    scripts.push(breadcrumbJsonLd)
  }

  // https://react-bootstrap.github.io/docs/getting-started/introduction
  const linkTags = [
    // {
    //   rel: 'stylesheet',
    //   href: 'https://cdn.jsdelivr.net/npm/bootstrap@5.3.2/dist/css/bootstrap.min.css',
    //   integrity: 'sha384-T3c6CoIi6uLrA9TneNEoa7RxnatzjcDSCmG1MXxSR1GAsXEV/Dwwykc2MPK8M2HN',
    //   crossorigin: 'anonymous',
    // }
  ]

  //   <script
  //   src='https://cdn.jsdelivr.net/npm/react/umd/react.production.min.js'
  //   crossorigin
  // ></script>

  // <script
  //   src='https://cdn.jsdelivr.net/npm/react-dom/umd/react-dom.production.min.js'
  //   crossorigin
  // ></script>

  // <script
  //   src='https://cdn.jsdelivr.net/npm/react-bootstrap@next/dist/react-bootstrap.min.js'
  //   crossorigin
  // ></script>

  // https://stackoverflow.com/questions/9975810/make-iframe-automatically-adjust-height-according-to-the-contents-without-using
  // <script>
  //   function resizeIframe(obj) {
  //     obj.style.height = obj.contentWindow.document.documentElement.scrollHeight + 'px';
  //   }
  // </script>

  // <script>
  //         {`
  //           function resizeIframe(obj) {
  //             obj.style.height = (obj.contentWindow.document.body.scrollHeight+5) + 'px';
  //           }
  //           function resizeIframe2(obj) {
  //             obj.style.height = obj.contentWindow.document.documentElement.scrollHeight + 'px';
  //           }
  //           function resizeIframe3(id) {
  //             console.log('resizeIframe3-1')
  //             if (document.getElementById) {
  //                 console.log('resizeIframe3-2')
  //                 var iframeid = document.getElementById(id); //iframe id
  //                 if (iframeid && !window.opera) {
  //                     if (iframeid.contentDocument && iframeid.contentDocument.body.offsetHeight) {
  //                         iframeid.height = iframeid.contentDocument.body.offsetHeight;
  //                     } else if (iframeid.Document && iframeid.Document.body.scrollHeight) {
  //                         iframeid.height = iframeid.Document.body.scrollHeight;
  //                     }
  //                 }
  //                 console.log('resizeIframe3: iframeid.contentWindow=', iframeid.contentWindow)
  //                 console.log('resizeIframe3: iframeid.contentDocument=', iframeid.contentDocument)
  //                 console.log('resizeIframe3: iframeid.Document=', iframeid.Document)

  //                 console.log('resizeIframe3: iframeid.contentWindow.document.body.scrollHeight=', iframeid.contentWindow.document.body.scrollHeight)
  //                 console.log('resizeIframe3: iframeid.contentWindow.document.body.offsetHeight=', iframeid.contentWindow.document.body.offsetHeight)
  //                 console.log('resizeIframe3: iframeid.contentWindow.document.documentElement.scrollHeight=', iframeid.contentWindow.document.documentElement.scrollHeight)
  //                 console.log('resizeIframe3: iframeid.contentWindow.document.documentElement.offsetHeight=', iframeid.contentWindow.document.documentElement.offsetHeight)
  //                 console.log('resizeIframe3: iframeid.contentDocument.body.scrollHeight=', iframeid.contentDocument.body.scrollHeight)
  //                 console.log('resizeIframe3: iframeid.contentDocument.body.offsetHeight=', iframeid.contentDocument.body.offsetHeight)
  //                 console.log('resizeIframe3: iframeid.Document.body.scrollHeight=', iframeid.Document.documentElement.scrollHeight)
  //                 console.log('resizeIframe3: iframeid.Document.body.offsetHeight=', iframeid.Document.documentElement.offsetHeight)
  //             }
  //             console.log('resizeIframe3-3')
  //           }
  //           function getIframeHeight(obj) {
  //             return this.contentWindow.document.body.scrollHeight+520
  //           }
  //         `}
  //       </script>

  return (
    <Helmet
      htmlAttributes={{
        // lang: locale || 'en'
        lang: locale || 'zh-Hant-TW'
      }}
      title={seoTitle || title}
      titleTemplate={`%s | ${site.title}`}
      meta={metaTags}
      script={scripts}
      link={linkTags}
    >
      <link rel='preconnect' href='https://fonts.googleapis.com' />
      <link rel='preconnect' href='https://fonts.gstatic.com' crossorigin />
      <link
        href='https://fonts.googleapis.com/css2?family=LXGW+WenKai+TC:wght@300;400;700&display=swap'
        rel='stylesheet'
      />

      {cacheControls && (
        <meta
          httpEquiv='Cache-Control'
          content='no-cache, no-store, must-revalidate'
        />
      )}

      {cacheControls && <meta httpEquiv='EXPIRES' content='0' />}

      {children}
    </Helmet>
  )
}

export default Seo

// <link href="https://fonts.googleapis.com/css2?family=Noto+Sans+TC:wght@100..900&display=swap" rel="stylesheet" />
//       <link href="https://fonts.googleapis.com/css2?family=Noto+Serif+TC:wght@200..900&display=swap" rel="stylesheet" />

// Reference
// https://www.gatsbyjs.com/docs/how-to/adding-common-features/adding-seo-component/
// https://www.gatsbyjs.com/docs/reference/built-in-components/gatsby-head/#editing-html-and-body
